import type { PropertyValueMap } from 'lit';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import '../elements/d-wrap';
import '../elements/d-action.js';
import '../elements/d-label.js';
import '../fields/d-expansion';
import { htmlContentStyles } from 'src/library/html-content-styles.js';
import { handleNavigation } from 'src/redux-behavior.js';
import { isEmptyHtmlContent, isSameHtmlContent } from 'src/utilities/text';
import type { ActionInput } from 'src/library/elements/action-input';

/**
 * Component for displaying HTML content with support for internal links and interactive checklists.
 *
 * Features:
 * - Renders HTML content using unsafeHTML directive
 * - Handles internal links with data-internal-link-id attribute for navigation
 * - Supports interactive checklists with clickable task items
 * - Allows text selection and copying without triggering checkbox toggles (issue #11684)
 * - Expandable/collapsible content sections
 * - Custom actions and labels
 *
 * Internal Link Processing:
 * Links with data-internal-link-id attributes are processed for internal navigation.
 * These links are created by the HTML editor (d-edit-html) with absolute href values
 * like '/account/123/assets/uuid' and are handled by the handleNavigation function.
 *
 * The internal linking flow:
 * 1. HTML editor creates links with absolute hrefs and data-internal-link-id
 * 2. convertInternalLinks() processes content based on document access permissions
 * 3. This component handles click events and navigates using the absolute href
 *
 * Text Selection Handling:
 * The clickHtml() method checks if user has selected text before processing clicks.
 * This prevents checkbox toggles when users copy text from checklist items or links.
 */
@customElement('d-view-html')
export class DViewHtml extends LitElement {
  static readonly styles = [
    css`
      :host {
        display: block;
      }

      :host * {
        outline: none;
      }

      d-label {
        margin-bottom: 8px;
      }

      .actions {
        margin-right: -2px;
      }

      d-action {
        margin: -6px 0;
      }

      :host * {
        -webkit-user-select: text;
        -ms-user-select: text;
        -moz-user-select: text;
        user-select: text;
      }

      #content {
        margin-top: 12px;
      }

      :host(.reduce-label-distance) #content {
        margin-top: 4px;
      }

      .templateDeleted .html {
        font-family: var(--mainSans);
        font-size: 15px;
        line-height: 150%;
      }

      body:not(.touch) d-event-view .html:not(.editable) ul.checklist > li:hover {
        cursor: pointer;
      }

      body:not(.touch) d-event-view .html:not(.editable) ul.checklist > li:hover:before {
        border-color: var(--borderColorDark);
      }
    `,
    htmlContentStyles,
  ];
  @property({ type: Boolean, attribute: 'prevent-checklist', reflect: true })
  opened = false;
  @property({ type: Boolean })
  expandable = false;
  @property({ type: Boolean })
  preventChecklist = false;
  @property({ type: Boolean, attribute: 'external-link-handler' })
  externalLinkHandler = false;
  @property({ type: String })
  label = '';
  @property({ type: String })
  field = '';
  @property({ type: String })
  sublabel = '';
  @property({ type: String })
  labelClasses = 'big';
  @property({ type: String })
  value = '';
  @property({ type: String, attribute: 'content-id' })
  contentId = '';
  @property({ type: Object })
  action: ActionInput | undefined = undefined;
  @property({ type: Array })
  pages = [];
  @property({ type: Boolean, reflect: true })
  big = false;
  @property({ type: Boolean, attribute: 'wrap-label', reflect: true })
  wrapLabel = false;
  @query('#content')
  private content!: HTMLElement;
  /**
   * Det som faktisk rendres. Skilles fra `value`-propen for at innkommende
   * verdier ikke skal klobre lokale, ulagrede sjekkliste-toggles: mens brukeren
   * toggler raskt kan propen ligge ett hakk bak lokal DOM (store → view-modell →
   * prop er asynkron), og en re-render med den etterslepende verdien vises som
   * flimmer (#13248).
   */
  @state()
  private renderValue = '';
  private checklistDirty = false;
  private lastRejectedValue?: string;
  private checklistFallbackTimer?: ReturnType<typeof setTimeout>;
  private static readonly CHECKLIST_FALLBACK_MS = 10_000;

  private clearChecklistDirty() {
    this.checklistDirty = false;
    this.lastRejectedValue = undefined;
    if (this.checklistFallbackTimer !== undefined) {
      clearTimeout(this.checklistFallbackTimer);
      this.checklistFallbackTimer = undefined;
    }
  }

  /**
   * Sikkerhetsventil: hvis ingen matchende verdi bekrefter de lokale togglene
   * (f.eks. lagringen feilet og d-app har refetchet server-sannheten), aksepteres
   * den sist avviste verdien — synlig revert i stedet for permanent utakt.
   */
  private armChecklistFallback() {
    if (this.checklistFallbackTimer !== undefined) {
      clearTimeout(this.checklistFallbackTimer);
    }
    this.checklistFallbackTimer = setTimeout(() => {
      this.checklistFallbackTimer = undefined;
      if (this.checklistDirty && this.lastRejectedValue !== undefined) {
        this.renderValue = this.lastRejectedValue;
        this.clearChecklistDirty();
      }
    }, DViewHtml.CHECKLIST_FALLBACK_MS);
  }

  protected willUpdate(changed: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (changed.has('contentId') && changed.get('contentId') !== undefined) {
      // Gjenbrukt element viser nå en annen entitet — lokal tilstand er ugyldig.
      this.clearChecklistDirty();
      this.renderValue = this.value;
    }
    if (changed.has('value')) {
      if (!this.checklistDirty) {
        this.renderValue = this.value;
      } else if (isSameHtmlContent(this.value, this.renderValue)) {
        // Innkommende verdi har tatt igjen lokal tilstand; behold renderValue
        // (visuelt identisk) så DOM-en ikke byttes ut unødig.
        this.clearChecklistDirty();
      } else {
        this.lastRejectedValue = this.value;
        this.armChecklistFallback();
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.checklistFallbackTimer !== undefined) {
      clearTimeout(this.checklistFallbackTimer);
      this.checklistFallbackTimer = undefined;
    }
  }

  renderLabel() {
    return html`
      <d-label
        classes="${this.labelClasses}"
        .label=${this.label}
        .field=${this.field}
        .sublabel=${this.sublabel}
        ?big=${this.big}
        ?allow-wrap=${this.wrapLabel}
      ></d-label>
    `;
  }

  render() {
    const expandingActionLabel = this.opened ? 'Skjul' : 'Vis';
    const expandingAction = this.expandable
      ? html` <d-action @click=${() => (this.opened = !this.opened)}>${expandingActionLabel}</d-action> `
      : nothing;
    const customAction = this.action
      ? html` <d-action .input=${this.action} @action=${(e) => this.handleAction(e)}></d-action> `
      : nothing;
    const optionalLabel =
      this.label !== '' || this.field !== ''
        ? html`
            ${this.expandable || this.action
              ? html`
                  <d-wrap split-reverse baseline>
                    <div class="actions">${customAction}${expandingAction}</div>
                    ${this.renderLabel()}
                  </d-wrap>
                `
              : html`<d-wrap>${this.renderLabel()}</d-wrap> `}
          `
        : nothing;
    const optionalHtmlContent = isEmptyHtmlContent(this.renderValue)
      ? nothing
      : html`
          <slot name="under-label"></slot>
          <div id="content" class="html" @click=${(e) => this.clickHtml(e)}>${unsafeHTML(this.renderValue)}</div>
        `;
    return html`
      ${optionalLabel}
      <slot name="under-label"></slot>
      <d-expansion .opened=${this.opened || !this.expandable}> ${optionalHtmlContent} </d-expansion>
    `;
  }

  async clickHtml(event: PointerEvent) {
    // If user has selected text within the HTML content, don't treat as click
    // This allows copy/paste from checklist items and links without toggling checkbox
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      // Check if selection is within the content element
      const range = selection.getRangeAt(0);
      if (this.content.contains(range.commonAncestorContainer)) {
        return;
      }
    }

    const target = event.target as HTMLElement;
    const a = target.closest('a');
    if (a) {
      const href = a.getAttribute('href');
      if (a.hasAttribute('data-internal-link-id')) {
        event.stopPropagation();
        event.preventDefault();
        const docType = a.getAttribute('data-internal-link-doctype');
        if (this.externalLinkHandler) {
          const uuid = a.getAttribute('data-internal-link-id');
          this.dispatchEvent(
            new CustomEvent('link-click', {
              bubbles: true,
              composed: true,
              detail: { docType, uuid, href },
            }),
          );
        } else {
          if (href) {
            window.history.pushState({}, '', href);
            await handleNavigation(window.location);
          }
        }
      }
    } else if (!this.preventChecklist) {
      const li = target.closest('li');
      if (li !== null && 'type' in li.dataset && li.dataset.type === 'taskItem') {
        event.stopPropagation();
        event.preventDefault();
        const c = li.dataset.checked === 'true';
        li.dataset.checked = c ? 'false' : 'true';
        const data = this.content.innerHTML;
        this.renderValue = data.replace(/<!--.*?-->/g, '');
        this.checklistDirty = true;
        // Fyres umiddelbart: koalescering/serialisering skjer i checklistSaveCoordinator,
        // som overlever at komponenten fjernes (navigering rett etter toggle).
        this.fireChecklistChanged();
      }
    }
  }

  handleAction(e) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent('action', {
        bubbles: true,
        composed: true,
        detail: e.detail,
      }),
    );
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('value')) {
      this.opened = false;
    }
  }

  private fireChecklistChanged() {
    this.dispatchEvent(
      new CustomEvent<{ content: string }>('checklist-changed', {
        composed: true,
        bubbles: true,
        detail: {
          content: this.renderValue,
        },
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-view-html': DViewHtml;
  }
}
